import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import { Badge } from 'src/shared/components/ui/badge';
import Autocomplete from 'src/shared/components/autocomplete/Autocomplete';
import type { SiteFilterCountry } from 'src/app/portfolio/state/api/siteFilterOptionsQuery';

type Props = {
    countries: SiteFilterCountry[];
    isLoading: boolean;
    selectedValues: string[];
    onChange: (values: string[]) => void;
};

const CountryFilterAccordion = ({ countries, isLoading, selectedValues, onChange }: Props) => {
    const [inputValue, setInputValue] = useState('');

    const options = countries.map(c => ({ value: c.code, label: c.name }));

    const handleSelect = (code: string | null) => {
        if (code && !selectedValues.includes(code)) {
            onChange([...selectedValues, code]);
        }
        setInputValue('');
    };

    const handleRemove = (code: string) => {
        onChange(selectedValues.filter(v => v !== code));
    };

    const nameFor = (code: string) => countries.find(c => c.code === code)?.name ?? code;

    return (
        <Accordion type='single' collapsible>
            <AccordionItem value='country'>
                <AccordionTrigger data-testid={selectedValues.length > 0 ? 'country-filter-count' : undefined}>
                    Country{selectedValues.length > 0 && ` (${selectedValues.length})`}
                </AccordionTrigger>
                <AccordionContent>
                    <div className='flex flex-col gap-2'>
                        {isLoading ? (
                            <span className='text-sm text-muted-foreground'>Loading countries…</span>
                        ) : (
                            <Autocomplete
                                options={options}
                                value=''
                                inputValue={inputValue}
                                onInputChange={setInputValue}
                                onChange={handleSelect}
                                placeholder='Search countries…'
                                showAllOnOpen
                                updateInputOnSelect={false}
                            />
                        )}
                        {selectedValues.length > 0 && (
                            <div className='flex flex-wrap gap-2' aria-label='Selected countries'>
                                {selectedValues.map(code => (
                                    <Badge key={code} variant='outline' className='flex items-center gap-1'>
                                        <span>{nameFor(code)}</span>
                                        <button
                                            type='button'
                                            aria-label={`Remove ${nameFor(code)}`}
                                            onClick={() => handleRemove(code)}
                                            className='inline-flex items-center justify-center text-muted-foreground hover:text-black'
                                        >
                                            <X className='h-3.5 w-3.5' />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};

export default CountryFilterAccordion;
